<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'clips' }"
          >Clips</router-link
        >
      </v-toolbar-title>
    </v-app-bar>
    <div class="ma-4">
      <div class="display-1 font-weight-bold my-5 text-capitalize">
        Update Clip
      </div>
      <v-form @submit.prevent="submit" ref="form">
        <div class="row">
          <div class="col-md-5">
            <v-divider class="pb-5"></v-divider>
            <label class="text-field-label">Title</label>
            <v-text-field
              flat
              solo
              required
              counter
              class="mt-2"
              :rules="rules.title"
              maxlength="255"
              v-model="form.title"
            ></v-text-field>

            <label class="text-field-label">Video</label>
            <v-select
              v-model="form.video_id"
              :items="videos"
              item-text="name"
              item-value="id"
              return-object
              solo
              flat
            ></v-select>

            <label class="text-field-label">Photo</label>
            <v-img
              v-if="photoCover"
              :src="photoCover"
              height="200"
              contain
              class="my-2"
            ></v-img>
            <v-file-input
              accept="image/png, image/jpeg, image/bmp"
              placeholder="Pick a new photo to update"
              :prepend-icon="icons.camera"
              v-model="form.photo"
            ></v-file-input>

            <v-divider class="my-5"></v-divider>
            <div class="mt-4">
              <v-btn
                color="primary"
                class="mr-4 px-6"
                height="40px"
                width="70%"
                @click="submit"
                :loading="form.$busy"
              >
                <v-icon left dark>{{ icons.update }}</v-icon
                >Update
              </v-btn>
            </div>
            <div class="mt-4">
              <v-btn
                @click="showDeleteModal = true"
                height="40px"
                width="70%"
                color="error"
              >
                <v-icon left dark>{{ icons.remove }}</v-icon
                >Delete Clip
              </v-btn>
            </div>
          </div>
          <div class="col-md-6">
            <v-card class="pa-4">
              <div class="title mb-4">Video Info</div>

              <div class="body-1 mb-2">
                <strong>Duration:</strong>
                {{
                  selectedVideo ? formatDuration(selectedVideo.duration) : '-'
                }}
              </div>
              <div class="body-1 mb-2">
                <strong>Filename:</strong>
                {{ selectedVideo ? selectedVideo.file_name : '-' }}
              </div>

              <div class="body-1 mb-2">
                <strong>Type:</strong>
                {{ selectedVideo ? selectedVideo.mime_type : '-' }}
              </div>

              <div class="body-1 mb-4">
                <strong>Size:</strong>
                {{ selectedVideo ? formatFileSize(selectedVideo.size) : '-' }}
              </div>
            </v-card>
          </div>
        </div>
      </v-form>
      <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text @click="snackbar.show = false" v-bind="attrs">OK</v-btn>
        </template>
      </v-snackbar>
      <ConfirmModal
        v-model="showDeleteModal"
        title="Delete Clip"
        message="Are you sure you want to delete this clip?"
        @cancel="showDeleteModal = false"
        @confirm="removeClip"
      ></ConfirmModal>
    </div>
  </div>
</template>

<script>
import Form from '@/utils/form'
import { mdiChevronLeft, mdiTrashCan, mdiUpdate, mdiCamera } from '@mdi/js'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import ConfirmModal from '@/components/modals/ConfirmModal'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'ClipDetails',
  components: {
    AppBarNavIcon,
    ConfirmModal,
  },
  data() {
    return {
      showDeleteModal: false,
      photoCover: null,
      icons: {
        arrowLeft: mdiChevronLeft,
        remove: mdiTrashCan,
        update: mdiUpdate,
        camera: mdiCamera,
      },
      rules: {
        title: [(v) => (v && v.length <= 255) || 'Max 255 characters'],
      },
      form: {
        title: '',
        video_id: null,
        photo: null,
      },
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
    }
  },
  computed: {
    ...mapState({
      clipDetails: (state) => state.clip.clipDetails,
      videos: (state) => state.video.list,
    }),
    selectedVideo() {
      if (!this.form || !this.form.video_id) return null

      if (typeof this.form.video_id === 'object') {
        return this.form.video_id
      }

      return this.clipDetails.video
    },
  },
  mounted() {
    this.getClip()
    this.fetchVideos()
  },
  methods: {
    ...mapActions({
      updateClip: 'clip/updateClip',
      deleteClip: 'clip/deleteClip',
      getClipDetails: 'clip/getClipDetails',
      getVideos: 'video/getVideos',
    }),
    async fetchVideos() {
      try {
        await this.getVideos({ page: 1, sort: 'created_at' }) // Example params
      } catch (error) {
        this.showSnackbar('Failed to load videos', 'error')
      }
    },
    async getClip() {
      await this.getClipDetails(this.$route.params.id)

      this.form = new Form({
        title: this.clipDetails.title || '',
        video_id: this.clipDetails.video_id || null,
        photo: null,
      })

      if (this.clipDetails.photo) {
        this.photoCover = this.clipDetails.photo.url
      }
    },
    formatDuration(seconds) {
      if (!seconds) return '00:00'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    async submit() {
      this.form.$busy = true
      this.form.$clearErrors()

      const formData = new FormData()

      formData.append('title', this.form.title)
      formData.append(
        'video_id',
        typeof this.form.video_id === 'object'
          ? this.form.video_id.id
          : this.form.video_id
      )

      if (this.form.photo) {
        formData.append('photo', this.form.photo)
      }

      formData.append('_method', 'PUT')

      const data = {
        data: formData,
        id: this.$route.params.id,
      }

      this.updateClip(data)
        .then(() => {
          this.showSnackbar('Clip successfully updated!', 'success')
          setTimeout(() => {
            this.$router.push({ name: 'clips' })
          }, 1000)
        })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
        .finally(() => (this.form.$busy = false))
    },
    removeClip() {
      this.deleteClip(this.$route.params.id)
        .then(() => {
          this.$router.push({ name: 'clips' })
        })
        .catch((error) =>
          this.showSnackbar(error.response.data.message, 'error')
        )
    },
    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },
}
</script>
